import { CollapsibleFilters } from '../../common/CollapsibleFilters';
import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormLabel,
  Input,
  Dropdown,
  Menu,
  MenuButton,
  MenuItem,
  Modal,
  ModalDialog,
  Option,
  Select,
  Stack,
  Typography,
} from "@mui/joy";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../lib/api";
import type { EmailTemplate } from "../../../types/marketing";
import { ResponsiveTable } from "../../common/ResponsiveTable";
import { EmailDesignWorkbench } from "./EmailDesignWorkbench";
import { EmailPreview } from "./EmailPreview";
import { emailError, type EmailPage } from "./emailWorkflows";
export function TemplatesTab() {
  const [search, setSearch] = useState("");
  const [archived, setArchived] = useState("false");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Partial<EmailTemplate> | null>(null);
  const [initial, setInitial] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [testFor, setTestFor] = useState<EmailTemplate | null>(null);
  const [testEmail, setTestEmail] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const cache = useQueryClient();
  const templates = useQuery({
    queryKey: ["email-templates", search, archived, page],
    queryFn: async () =>
      (
        await api.get<EmailPage<EmailTemplate>>(
          `/api/marketing/email-templates/?${new URLSearchParams({ search, archived, page: String(page) })}`,
        )
      ).data,
  });
  const open = (template: Partial<EmailTemplate>) => {
    setEditing(template);
    setInitial(JSON.stringify(template));
    setError("");
  };
  const close = () => {
    if (
      !busy &&
      (!editing ||
        JSON.stringify(editing) === initial ||
        window.confirm("Discard unsaved template changes?"))
    )
      setEditing(null);
  };
  const refresh = () =>
    cache.invalidateQueries({ queryKey: ["email-templates"] });
  const run = async (action: () => Promise<unknown>, message: string) => {
    setBusy(true);
    setError("");
    try {
      await action();
      await refresh();
      setNotice(message);
      return true;
    } catch (cause) {
      setError(emailError(cause));
      return false;
    } finally {
      setBusy(false);
    }
  };
  const save = async () => {
    if (!editing) return;
    const payload = {
      name: editing.name,
      subject: editing.subject,
      preview_text: editing.preview_text,
      html_content: editing.html_content,
    };
    if (
      await run(
        () =>
          editing.id
            ? api.updateEmailTemplate(editing.id, payload)
            : api.createEmailTemplate(payload),
        "Template saved.",
      )
    )
      setEditing(null);
  };
  return (
    <Stack spacing={2}>
      <Button
          onClick={() =>
            open({
              name: "",
              subject: "",
              preview_text: "",
              html_content: "<p></p>",
            })
          }
        >
          New template
        </Button>
<CollapsibleFilters><Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <Input
          aria-label="Search templates"
          placeholder="Search templates"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          sx={{ flex: 1 }}
        />
        <Select
          aria-label="Template status"
          value={archived}
          onChange={(_, value) => {
            setArchived(value ?? "");
            setPage(1);
          }}
        >
          <Option value="false">Active templates</Option>
          <Option value="true">Archived templates</Option>
          <Option value="">All templates</Option>
        </Select>

      </Stack></CollapsibleFilters>
      {error && <Alert color="danger">{error}</Alert>}
      {notice && <Alert color="success">{notice}</Alert>}
      {templates.isError && (
        <Alert
          color="danger"
          endDecorator={
            <Button onClick={() => templates.refetch()}>Retry</Button>
          }
        >
          Templates could not be loaded.
        </Alert>
      )}
      <ResponsiveTable
        rows={templates.data?.results || []}
        getRowKey={(template) => template.id}
        columns={[
          {
            key: "name",
            header: "Template",
            mobileVariant: "header",
            render: (t) => <Typography level="title-sm">{t.name}</Typography>,
          },
          { key: "subject", header: "Subject", render: (t) => t.subject },
          {
            key: "status",
            header: "Status",
            render: (t) => (
              <Chip size="sm">{t.is_archived ? "Archived" : "Active"}</Chip>
            ),
          },
        ]}
        actionsWidth="160px"
        rowActions={(template) => (
          <Stack direction="row" spacing={1}>
            <Button size="sm" variant="outlined" onClick={() => open(template)}>
              Edit
            </Button>
            <Dropdown>
              <MenuButton size="sm" variant="plain">
                More
              </MenuButton>
              <Menu>
                <MenuItem
                  disabled={busy}
                  onClick={() =>
                    run(
                      () =>
                        api.post(
                          `/api/marketing/email-templates/${template.id}/duplicate/`,
                          {},
                        ),
                      "Template duplicated.",
                    )
                  }
                >
                  Duplicate template
                </MenuItem>
                <MenuItem
                  disabled={busy}
                  onClick={() =>
                    run(
                      () =>
                        api.updateEmailTemplate(template.id, {
                          is_archived: !template.is_archived,
                        }),
                      template.is_archived
                        ? "Template restored."
                        : "Template archived.",
                    )
                  }
                >
                  {template.is_archived
                    ? "Restore template"
                    : "Archive template"}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setTestFor(template);
                    setConfirmed(false);
                  }}
                >
                  Send a test email
                </MenuItem>
              </Menu>
            </Dropdown>
          </Stack>
        )}
        pagination={{
          pageSize: 20,
          page,
          totalCount: templates.data?.count || 0,
          onPageChange: setPage,
          itemNoun: "templates",
        }}
        emptyState={
          <Typography>
            {templates.isPending
              ? "Loading templates…"
              : templates.isError
                ? "Templates unavailable."
                : "No matching templates."}
          </Typography>
        }
      />
      <Modal open={!!editing} onClose={close}>
        <ModalDialog
          sx={{
            width: 1100,
            maxWidth: "96vw",
            maxHeight: "94vh",
            overflowY: "auto",
          }}
        >
          <Typography level="h4">
            {editing?.id ? "Edit template" : "Create template"}
          </Typography>
          {error && <Alert color="danger">{error}</Alert>}
          {editing && (
            <Stack spacing={2}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 2,
                }}
              >
                {[
                  ["Name", "name"],
                  ["Subject", "subject"],
                  ["Inbox preview text", "preview_text"],
                ].map(([label, key]) => (
                  <FormControl key={key}>
                    <FormLabel>{label}</FormLabel>
                    <Input
                      value={String(editing[key as keyof EmailTemplate] || "")}
                      disabled={busy}
                      onChange={(event) =>
                        setEditing({ ...editing, [key]: event.target.value })
                      }
                    />
                  </FormControl>
                ))}
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    lg: "minmax(0,1fr) minmax(0,1fr)",
                  },
                  gap: 3,
                }}
              >
                <EmailDesignWorkbench
                  value={editing.html_content || ""}
                  onChange={(html) =>
                    setEditing({ ...editing, html_content: html })
                  }
                />
                <EmailPreview
                  html={editing.html_content || ""}
                  subject={editing.subject}
                  preheader={editing.preview_text}
                />
              </Box>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button variant="plain" disabled={busy} onClick={close}>
                  Cancel
                </Button>
                <Button
                  loading={busy}
                  disabled={
                    !editing.name?.trim() ||
                    !editing.subject?.trim() ||
                    !editing.html_content?.trim()
                  }
                  onClick={save}
                >
                  Save template
                </Button>
              </Stack>
            </Stack>
          )}
        </ModalDialog>
      </Modal>
      <Modal
        open={!!testFor}
        onClose={() => {
          if (!busy) setTestFor(null);
        }}
      >
        <ModalDialog sx={{ width: 500, maxWidth: "94vw" }}>
          <Typography level="h4">Send a test email</Typography>
          {error && <Alert color="danger">{error}</Alert>}
          <Typography>
            Send “{testFor?.name}” to yourself or a test address. This sends a
            real email.
          </Typography>
          <FormControl>
            <FormLabel>Test address (blank uses your account email)</FormLabel>
            <Input
              type="email"
              value={testEmail}
              onChange={(event) => setTestEmail(event.target.value)}
            />
          </FormControl>
          <Checkbox
            checked={confirmed}
            onChange={(event) => setConfirmed(event.target.checked)}
            label="I’m ready to send this test email"
          />
          <Stack direction="row" spacing={1}>
            <Button
              variant="plain"
              onClick={() => setTestFor(null)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              disabled={!confirmed}
              loading={busy}
              onClick={async () => {
                if (
                  testFor &&
                  (await run(
                    () => api.sendTestEmail(testFor.id, testEmail || undefined),
                    "Test email sent.",
                  ))
                )
                  setTestFor(null);
              }}
            >
              Send test
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </Stack>
  );
}
